import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import api from '../../utils/api';
import { toast } from 'react-toastify';
import { FiSettings, FiUser, FiLock, FiBell, FiShield, FiSave, FiInfo, FiServer, FiCpu, FiClock, FiDatabase, FiUsers, FiMessageCircle, FiActivity } from 'react-icons/fi';

/* ══ CONQUER Cyan Theme ══ */
const THEME = {
  primary: '#00B4D8',
  secondary: '#48CAE4',
  accent: '#90E0EF',
  success: '#059669',
  warning: '#D97706',
  danger: '#DC2626',
  dark: '#0A1628',
  slate: '#0096C7',
  muted: '#48CAE4',
};

const SectionHeader = ({ icon: Icon, title, subtitle }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
    <div style={{ width: 40, height: 40, borderRadius: 12, background: `${THEME.primary}15`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Icon size={20} style={{ color: THEME.primary }} />
    </div>
    <div>
      <h3 style={{ fontSize: 16, fontWeight: 700, color: THEME.dark, margin: 0 }}>{title}</h3>
      {subtitle && <p style={{ fontSize: 12, color: THEME.muted, margin: '2px 0 0' }}>{subtitle}</p>}
    </div>
  </div>
);

const Toggle = ({ checked, onChange, label }) => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 0', borderBottom: '1px solid #F0EEFF' }}>
    <span style={{ fontSize: 14, fontWeight: 500, color: THEME.dark }}>{label}</span>
    <button
      type="button"
      onClick={() => onChange(!checked)}
      style={{
        width: 48, height: 26, borderRadius: 13, border: 'none', cursor: 'pointer',
        background: checked ? `linear-gradient(135deg, ${THEME.primary}, ${THEME.secondary})` : '#E0E7FF',
        position: 'relative', transition: 'background 0.2s',
      }}
    >
      <span style={{
        width: 20, height: 20, borderRadius: '50%', background: '#fff',
        position: 'absolute', top: 3, left: checked ? 25 : 3,
        transition: 'left 0.2s', boxShadow: '0 1px 4px rgba(0,0,0,0.15)',
      }} />
    </button>
  </div>
);

const SystemStat = ({ icon: Icon, label, value, color }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 0', borderBottom: '1px solid #F0EEFF' }}>
    <div style={{ width: 36, height: 36, borderRadius: 10, background: `${color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Icon size={18} style={{ color }} />
    </div>
    <div style={{ flex: 1 }}>
      <div style={{ fontSize: 12, color: THEME.muted }}>{label}</div>
      <div style={{ fontSize: 14, fontWeight: 700, color: THEME.dark }}>{value}</div>
    </div>
  </div>
);

export default function AdminSettings() {
  const { user } = useAuth();

  const [profile, setProfile] = useState({ username: user?.username || '', email: user?.email || '' });
  const [passwords, setPasswords] = useState({ current: '', newPass: '', confirm: '' });
  const [notifications, setNotifications] = useState({ loginAlerts: true, campaignComplete: true, failureAlerts: true, dailyDigest: false });
  const [loadingNotifs, setLoadingNotifs] = useState(true);
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [systemInfo, setSystemInfo] = useState(null);
  const [loadingSystem, setLoadingSystem] = useState(true);

  const fetchNotificationPrefs = useCallback(async () => {
    setLoadingNotifs(true);
    try {
      const res = await api.get('/auth/notification-prefs');
      setNotifications({
        loginAlerts: res.data.loginAlerts ?? true,
        campaignComplete: res.data.campaignComplete ?? true,
        failureAlerts: res.data.failureAlerts ?? true,
        dailyDigest: res.data.dailyDigest ?? false,
      });
    } catch {}
    finally { setLoadingNotifs(false); }
  }, []);

  const updateNotification = async (key, value) => {
    const updated = { ...notifications, [key]: value };
    setNotifications(updated);
    try { await api.put('/auth/notification-prefs', { [key]: value }); toast.success('Preference saved'); }
    catch { setNotifications(notifications); toast.error('Failed to save preference'); }
  };

  const fetchSystemInfo = useCallback(async () => {
    setLoadingSystem(true);
    try {
      const res = await api.get('/admin/system-info');
      setSystemInfo(res.data);
    } catch { setSystemInfo(null); }
    finally { setLoadingSystem(false); }
  }, []);

  useEffect(() => { fetchSystemInfo(); fetchNotificationPrefs(); }, [fetchSystemInfo, fetchNotificationPrefs]);

  const handleProfileSave = async () => {
    setSavingProfile(true);
    try { await api.put('/auth/profile', { email: profile.email }); toast.success('Profile updated!'); }
    catch (err) { toast.error(err.response?.data?.message || 'Update failed'); }
    finally { setSavingProfile(false); }
  };

  const handlePasswordChange = async () => {
    if (passwords.newPass !== passwords.confirm) return toast.error('Passwords do not match');
    if (passwords.newPass.length < 6) return toast.error('Password must be at least 6 characters');
    setSavingPassword(true);
    try {
      await api.put('/auth/profile', { currentPassword: passwords.current, newPassword: passwords.newPass });
      toast.success('Password changed successfully!');
      setPasswords({ current: '', newPass: '', confirm: '' });
    } catch (err) { toast.error(err.response?.data?.message || 'Password change failed'); }
    finally { setSavingPassword(false); }
  };

  const inputStyle = {
    width: '100%', padding: '12px 14px', borderRadius: 10, border: '2px solid #E0E7FF',
    fontSize: 14, color: THEME.dark, background: '#FAFAFF', outline: 'none',
  };

  const cardStyle = {
    background: '#fff', borderRadius: 18, padding: 24,
    border: `1px solid ${THEME.accent}30`, boxShadow: '0 4px 24px rgba(124,58,237,0.08)',
  };

  return (
    <div style={{ padding: '0 4px' }}>
      {/* Header */}
      <div style={{ marginBottom: 28 }}>
        <h1 style={{ fontSize: 26, fontWeight: 800, color: THEME.dark, margin: 0, display: 'flex', alignItems: 'center', gap: 10 }}>
          <FiSettings size={28} style={{ color: THEME.primary }} />
          Settings
        </h1>
        <p style={{ fontSize: 14, color: THEME.muted, marginTop: 6 }}>Manage your account, security & preferences</p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 }}>
        {/* Profile Section */}
        <div style={cardStyle}>
          <SectionHeader icon={FiUser} title="Profile" subtitle="Your account details" />

          <div style={{
            display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24, padding: 16, borderRadius: 14,
            background: `linear-gradient(135deg, ${THEME.primary}10, ${THEME.accent}10)`,
          }}>
            <div style={{
              width: 56, height: 56, borderRadius: 14,
              background: `linear-gradient(135deg, ${THEME.primary}, ${THEME.secondary})`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 22, fontWeight: 800, color: '#fff',
            }}>{user?.username?.[0]?.toUpperCase() || 'A'}</div>
            <div>
              <div style={{ fontSize: 16, fontWeight: 700, color: THEME.dark }}>{user?.username}</div>
              <div style={{ fontSize: 12, color: THEME.muted }}>{user?.email}</div>
              <span style={{
                fontSize: 10, fontWeight: 700, padding: '3px 10px', borderRadius: 8,
                background: `${THEME.primary}15`, color: THEME.primary, textTransform: 'uppercase', letterSpacing: 0.5,
              }}>{user?.role}</span>
            </div>
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: THEME.muted, display: 'block', marginBottom: 6 }}>Username</label>
            <input type="text" style={inputStyle} value={profile.username}
              onChange={e => setProfile({ ...profile, username: e.target.value })} />
          </div>
          <div style={{ marginBottom: 16 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: THEME.muted, display: 'block', marginBottom: 6 }}>Email</label>
            <input type="email" style={inputStyle} value={profile.email}
              onChange={e => setProfile({ ...profile, email: e.target.value })} />
          </div>
          <button onClick={handleProfileSave} disabled={savingProfile}
            style={{
              padding: '12px 24px', borderRadius: 10, border: 'none',
              background: `linear-gradient(135deg, ${THEME.primary}, ${THEME.secondary})`,
              color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: 8,
              boxShadow: `0 4px 12px ${THEME.primary}30`,
              opacity: savingProfile ? 0.7 : 1,
            }}>
            <FiSave size={16} /> {savingProfile ? 'Saving...' : 'Save Profile'}
          </button>
        </div>

        {/* Security Section */}
        <div style={cardStyle}>
          <SectionHeader icon={FiLock} title="Security" subtitle="Change your password" />

          <div style={{
            padding: 14, borderRadius: 12,
            background: `${THEME.slate}10`, border: `1px solid ${THEME.slate}20`,
            marginBottom: 20, display: 'flex', gap: 10, alignItems: 'flex-start',
          }}>
            <FiInfo style={{ color: THEME.slate, fontSize: 16, marginTop: 2, flexShrink: 0 }} />
            <div style={{ fontSize: 12, color: THEME.slate, lineHeight: 1.6 }}>
              Password must be at least 6 characters. Use a mix of letters, numbers, and symbols for better security.
            </div>
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: THEME.muted, display: 'block', marginBottom: 6 }}>Current Password</label>
            <input type="password" style={inputStyle} value={passwords.current} placeholder="Enter current password"
              onChange={e => setPasswords({ ...passwords, current: e.target.value })} />
          </div>
          <div style={{ marginBottom: 16 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: THEME.muted, display: 'block', marginBottom: 6 }}>New Password</label>
            <input type="password" style={inputStyle} value={passwords.newPass} placeholder="Enter new password"
              onChange={e => setPasswords({ ...passwords, newPass: e.target.value })} />
          </div>
          <div style={{ marginBottom: 16 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: THEME.muted, display: 'block', marginBottom: 6 }}>Confirm New Password</label>
            <input type="password" style={inputStyle} value={passwords.confirm} placeholder="Confirm new password"
              onChange={e => setPasswords({ ...passwords, confirm: e.target.value })} />
          </div>
          <button onClick={handlePasswordChange} disabled={savingPassword}
            style={{
              padding: '12px 24px', borderRadius: 10, border: 'none',
              background: `linear-gradient(135deg, ${THEME.primary}, ${THEME.secondary})`,
              color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: 8,
              boxShadow: `0 4px 12px ${THEME.primary}30`,
              opacity: savingPassword ? 0.7 : 1,
            }}>
            <FiShield size={16} /> {savingPassword ? 'Changing...' : 'Change Password'}
          </button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
        {/* Notification Preferences */}
        <div style={cardStyle}>
          <SectionHeader icon={FiBell} title="Notifications" subtitle="Control what alerts you receive" />
          <Toggle label="Login alerts" checked={notifications.loginAlerts} onChange={v => updateNotification('loginAlerts', v)} />
          <Toggle label="Campaign completion alerts" checked={notifications.campaignComplete} onChange={v => updateNotification('campaignComplete', v)} />
          <Toggle label="High failure rate warnings" checked={notifications.failureAlerts} onChange={v => updateNotification('failureAlerts', v)} />
          <Toggle label="Daily digest email" checked={notifications.dailyDigest} onChange={v => updateNotification('dailyDigest', v)} />
          {loadingNotifs && <p style={{ fontSize: 11, color: THEME.muted, margin: '12px 0 0', fontStyle: 'italic' }}>Loading preferences...</p>}
        </div>

        {/* System Info */}
        <div style={cardStyle}>
          <SectionHeader icon={FiServer} title="System Info" subtitle="Dynamic system statistics" />
          {loadingSystem ? (
            <div style={{ padding: 40, textAlign: 'center' }}>
              <div style={{ width: 36, height: 36, border: '3px solid #E0E7FF', borderTopColor: THEME.primary, borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto' }} />
              <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            </div>
          ) : systemInfo ? (
            <>
              <SystemStat icon={FiCpu} label="Environment" value={systemInfo.nodeEnv || 'production'} color={THEME.primary} />
              <SystemStat icon={FiClock} label="Server Uptime" value={systemInfo.uptime || '—'} color={THEME.success} />
              <SystemStat icon={FiDatabase} label="Database" value={systemInfo.dbStatus || 'Connected'} color={THEME.slate} />
              <SystemStat icon={FiUsers} label="Total Users" value={systemInfo.totalUsers?.toLocaleString() || '—'} color={THEME.secondary} />
              <SystemStat icon={FiMessageCircle} label="Total Campaigns" value={systemInfo.totalCampaigns?.toLocaleString() || '—'} color={THEME.warning} />
              <SystemStat icon={FiActivity} label="Messages Today" value={systemInfo.messagesToday?.toLocaleString() || '—'} color={THEME.success} />
            </>
          ) : (
            <div style={{ textAlign: 'center', padding: 30, color: THEME.muted }}>
              <FiServer size={36} style={{ marginBottom: 10, opacity: 0.4 }} />
              <div style={{ fontSize: 14 }}>Unable to load system info</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
